// Re-authentication: when the API answers 403 reauthentication_required, this dialog asks for the
// password (or a TOTP code), calls /auth/reauthenticate, and the original request is retried once.

import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Field, Input, Modal } from '../components/ui.tsx';
import { useI18n } from '../i18n/index.ts';
import { api, ApiError, onReauthRequired } from './api.ts';
import { useMe } from './queries.ts';

export function ReauthDialog() {
  const { t } = useI18n();
  const me = useMe().data;
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState('');
  const [useCode, setUseCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pending = useRef<((ok: boolean) => void) | null>(null);

  useEffect(() => {
    onReauthRequired(() => new Promise<boolean>((resolve) => {
      pending.current?.(false);
      pending.current = resolve;
      setSecret('');
      setError(null);
      setOpen(true);
    }));
    return () => onReauthRequired(null);
  }, []);

  const finish = (ok: boolean) => {
    setOpen(false);
    pending.current?.(ok);
    pending.current = null;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/reauthenticate', { method: 'POST', body: useCode ? { totp_code: secret } : { password: secret } });
      finish(true);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? t('errors.rateLimited') : t('reauth.wrong'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} title={t('reauth.title')} onClose={() => finish(false)}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p className="text-sm text-slate-600">{t('reauth.why')}</p>
        {error && <Alert tone="red">{error}</Alert>}
        <Field label={useCode ? t('auth.totpCode') : t('auth.password')}>
          {(p) => (
            <Input {...p} type={useCode ? 'text' : 'password'} inputMode={useCode ? 'numeric' : undefined}
              autoComplete={useCode ? 'one-time-code' : 'current-password'} value={secret} autoFocus
              onChange={(e) => setSecret(e.target.value)} required />
          )}
        </Field>
        <div className="flex items-center justify-between">
          {me?.two_factor_enabled ? (
            <button type="button" className="text-sm text-brand-600 hover:underline" onClick={() => setUseCode(!useCode)}>
              {useCode ? t('reauth.usePassword') : t('reauth.useCode')}
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => finish(false)}>{t('common.cancel')}</Button>
            <Button type="submit" loading={busy}>{t('reauth.confirm')}</Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
